/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import { percentageOffTotal as getPercentageOffTotal } from '@thd-olt-component-react/product-set';
import {
  ACCESSORY_CATEGORIES,
  DEFAULT_GROUP,
  DEFAULT_ACCESSORY_HEADING,
  ACCESSORY_HEADER_MAPPING,
  ANALYTICS_NOT_APPLICABLE,
  BUNDLE_TYPES,
  DELIVERY_MESSAGING
} from './constants';

export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

const getBundleType = (bundleDescription) => {
  switch (bundleDescription) {
  case 'Complete Your Laundry Room':
    return BUNDLE_TYPES.LAUNDRY;
  case 'Complete Your Kitchen':
    return BUNDLE_TYPES.KITCHEN;
  default:
    return BUNDLE_TYPES.DEFAULT;
  }
};

const processItemQuantities = (items = []) => {
  return items?.map((item) => {
    return {
      ...item,
      defaultQuantity: item?.defaultQuantity || 1
    };
  });
};

const groupBundleItems = (bundleItems) => {
  return bundleItems?.reduce((bundledItems, product) => {
    const group = product.category || product.slot?.order || DEFAULT_GROUP;
    if (bundledItems[group]) {
      bundledItems[group].push(product);
    } else {
      bundledItems[group] = [product];
    }
    return bundledItems;
  }, {});
};

function performLogicToConstructHeader(titleText, numAccessoryItems, numNonAccessoryItems, constructedHeader, position) {
  if (titleText === undefined) {
    return DEFAULT_ACCESSORY_HEADING;
  }
  if (numAccessoryItems === 1) {
    const numNonAccessoryItemsLogic = numNonAccessoryItems > 1 ? 'them' : 'it';
    return constructedHeader + ` ${numNonAccessoryItemsLogic} ${titleText}?`;
  }
  if (numAccessoryItems <= 2 && position < numAccessoryItems) {
    return constructedHeader + `${titleText} or`;
  }
  if (numAccessoryItems <= 2) {
    return constructedHeader + ` ${titleText}?`;
  }
  if (position < numAccessoryItems) {
    return constructedHeader + ` ${titleText},`;
  }

  return constructedHeader + `or ${titleText}?`;
}

const buildAccessoryHeading = ({ accessoryItems, numNonAccessoryItems }) => {
  const numAccessoryItems = accessoryItems.length;
  const selectionHeader = numAccessoryItems > 1 ? '' : 'Want';
  return numAccessoryItems === 0 ? '' : accessoryItems.reduce((constructedHeader, product, index) => {
    const position = index + 1;
    const titleText = ACCESSORY_HEADER_MAPPING[product.category];

    return performLogicToConstructHeader(titleText, numAccessoryItems, numNonAccessoryItems, constructedHeader, position);
  }, selectionHeader);
};

export const structureBundle = (bundleData, clientProductData, productData) => {
  const bundle = processItemQuantities(bundleData?.bundle?.products);
  const nonAnchorItems = bundle?.filter(({ isAnchor }) => !isAnchor);
  const bundleItems = nonAnchorItems?.filter(({ category }) => !ACCESSORY_CATEGORIES.includes(category));
  const accessoryItems = nonAnchorItems?.filter(({ category }) => ACCESSORY_CATEGORIES.includes(category));

  const anchorData = clientProductData?.product || productData?.product || {};
  const bundleAnchor = bundle?.find(({ isAnchor }) => isAnchor);
  const anchorItem = {
    ...bundleAnchor,
    product: {
      ...bundleAnchor?.product,
      fulfillment: anchorData?.fulfillment,
      availabilityType: anchorData?.availabilityType
    }
  };

  const bundleDescription = bundleItems[0]?.slot?.description || accessoryItems[0]?.slot?.description;

  return {
    anchorItem,
    groupedBundleItems: groupBundleItems(bundleItems),
    accessoryItems,
    entireBundleHeading: bundleDescription,
    accessoryHeading: buildAccessoryHeading({
      accessoryItems,
      numNonAccessoryItems: (bundleItems?.length || -1) + 1
    }),
    bundleType: getBundleType(bundleDescription)
  };
};

const buildFullItems = (items) => {
  return items.reduce((newItems, item) => {
    newItems.push(...Array(item?.defaultQuantity).fill(item));
    return newItems;
  }, []);
};

export const getCart = (channel, items = []) => {
  const cartItems = buildFullItems(items);
  return {
    cartOptions: {
      host: typeof window !== 'undefined'
        ? window.location.origin
        : null,
      channel,
      paypal: false,
      misship: false,
      directCheckout: false
    },
    cartReqParams: cartItems.map(({ product }) => {
      return {
        itemId: String(product.itemId),
        quantity: 1,
        fulfillmentMethod: 'DirectDelivery',
        fulfillmentLocation: '',
      };
    })
  };
};

export const getDeliveryMessaging = (
  price, enableApplianceDeliveryCharge, enableFreeDeliveryForExchange, isExchangeCustomer, applianceDeliveryThreshold
) => {
  if ((price >= applianceDeliveryThreshold && !enableApplianceDeliveryCharge)
     || (isExchangeCustomer && enableFreeDeliveryForExchange)) {
    return {
      deliveryMessage: DELIVERY_MESSAGING.FREE,
      showIcon: true,
      showMessaging: true
    };
  }
  if (enableApplianceDeliveryCharge) {
    return {
      deliveryMessage: DELIVERY_MESSAGING.CHARGE,
      showIcon: false,
      showMessaging: false
    };
  }
  return {
    deliveryMessage: DELIVERY_MESSAGING.THRESHOLD,
    showIcon: true,
    showMessaging: true
  };
};

function performMapLogic(item, MAP) {
  if (!item.checked) {
    return MAP;
  }
  if (item.pricingMessage === 'see final price in cart' && item.mapAboveOriginalPrice) {
    MAP = true;
  }
  return MAP;
}

// Parts of this can probably be simplified further
export const getPricing = (items = [], mapDetail) => {
  const cartItems = buildFullItems(items);
  const hidePromotionDetails = cartItems.some(({ product }) => !product?.pricing?.original);
  let originalPriceTotal = '';
  let dollarOffTotal = '';
  let percentageOffTotal = '';

  if (!hidePromotionDetails) {
    // originalPrice total
    originalPriceTotal = cartItems.reduce((totalPrice, { product }) => {
      return totalPrice + (product?.pricing?.original || product?.pricing?.value);
    }, 0);
    originalPriceTotal = parseFloat(originalPriceTotal.toFixed(2));
    // dollarOff total
    dollarOffTotal = cartItems.reduce((totalOff, { product }) => {
      const dollarOff = product?.pricing?.promotion?.dollarOff
        ? product?.pricing?.promotion?.dollarOff
        : 0;
      return totalOff + dollarOff;
    }, 0);
    dollarOffTotal = parseFloat(dollarOffTotal.toFixed(2));
    // percentageOff total
    percentageOffTotal = originalPriceTotal === 0
      ? 100
      : (dollarOffTotal / originalPriceTotal) * 100;
    percentageOffTotal = percentageOffTotal.toFixed();
  }

  // specialPrice total
  let valuePriceTotal = cartItems.reduce((totalPrice, { product }) => {
    return totalPrice + product?.pricing?.value;
  }, 0);
  valuePriceTotal = parseFloat(valuePriceTotal.toFixed(2));
  // pricingMessage
  let pricingMessage = cartItems.reduce((message, { product }) => {
    if (product?.pricing?.message) {
      return product?.pricing?.message;
    }
    return message;
  }, '');
  const seeFinalPriceMsgCount = cartItems.filter(
    ({ product }) => (product?.pricing?.mapScenario || product?.pricing?.message === 'see final price in cart')
  ).length;
  if (seeFinalPriceMsgCount > 0) {
    pricingMessage = 'see final price in cart';
  }
  const mapPricing = cartItems.reduce((MAP, item) => {
    return performMapLogic(item, MAP);
  }, false);

  return {
    pricing: {
      original: originalPriceTotal,
      promotion: {
        dollarOff: dollarOffTotal,
        percentageOff: percentageOffTotal,
      },
      value: valuePriceTotal,
      message: pricingMessage,
      mapAboveOriginalPrice: mapPricing,
      mapDetail: {
        ...mapDetail,
        percentageOffTotal: getPercentageOffTotal(originalPriceTotal, mapDetail?.dollarOff)
      }
    }
  };
};

export const onAddToCartClick = (products, metadata) => {
  if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS) {
    const {
      modelName = ANALYTICS_NOT_APPLICABLE,
      version = ANALYTICS_NOT_APPLICABLE,
      apiName = ANALYTICS_NOT_APPLICABLE
    } = metadata;

    window.LIFE_CYCLE_EVENT_BUS.trigger('fbt.add-to-cart', {
      bundle: true,
      items: products,
      modelName,
      version,
      apiName
    });
  }
};
